import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { acceptInvitation, ApiError } from './api';
import { BrandLockup } from './BrandLockup';
import { SupportFooter } from './SupportFooter';

interface InvitationFields {
  firstName: string;
  lastName: string;
  password: string;
  passwordConfirmation: string;
}

export function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const fragmentToken = new URLSearchParams(location.hash.replace(/^#/, '')).get('token');
  const token = fragmentToken ?? searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { register, handleSubmit, watch, formState } = useForm<InvitationFields>();
  const mutation = useMutation({
    mutationFn: (values: InvitationFields) =>
      acceptInvitation({
        token,
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password,
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      const destination =
        result.user.role === 'PARTNER'
          ? '/crm/companies'
          : result.user.role === 'CLIENT'
            ? '/portal'
            : '/crm/projects';
      void navigate(destination, { replace: true });
    },
  });

  if (!token) return <Navigate replace to="/login" />;

  return (
    <Container component="main" maxWidth="sm" sx={{ py: { xs: 5, md: 10 } }}>
      <Stack spacing={4}>
        <Box>
          <Box
            sx={{
              bgcolor: 'primary.dark',
              borderRadius: 2.5,
              display: 'inline-flex',
              px: 2,
              py: 1.5,
            }}
          >
            <BrandLockup />
          </Box>
          <Typography component="h1" sx={{ mt: 1 }} variant="h2">
            Attiva il tuo account.
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Conferma i dati nominativi e scegli una password personale per accedere alla
            piattaforma.
          </Typography>
        </Box>
        <Card>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Box
              component="form"
              onSubmit={(event) => void handleSubmit((values) => mutation.mutate(values))(event)}
            >
              <Stack spacing={3}>
                <TextField
                  autoComplete="given-name"
                  autoFocus
                  error={Boolean(formState.errors.firstName)}
                  fullWidth
                  label="Nome"
                  {...register('firstName', { required: true, maxLength: 100 })}
                />
                <TextField
                  autoComplete="family-name"
                  error={Boolean(formState.errors.lastName)}
                  fullWidth
                  label="Cognome"
                  {...register('lastName', { required: true, maxLength: 100 })}
                />
                <TextField
                  autoComplete="new-password"
                  error={Boolean(formState.errors.password)}
                  fullWidth
                  helperText="Almeno 12 caratteri; usa una password unica."
                  label="Nuova password"
                  type="password"
                  {...register('password', { required: true, minLength: 12, maxLength: 128 })}
                />
                <TextField
                  autoComplete="new-password"
                  error={Boolean(formState.errors.passwordConfirmation)}
                  fullWidth
                  label="Conferma password"
                  type="password"
                  {...register('passwordConfirmation', {
                    required: true,
                    validate: (value) => value === watch('password'),
                  })}
                />
                <Button
                  disabled={mutation.isPending}
                  size="large"
                  startIcon={
                    mutation.isPending ? <CircularProgress size={18} /> : <CheckCircleRoundedIcon />
                  }
                  type="submit"
                  variant="contained"
                >
                  Attiva account
                </Button>
              </Stack>
            </Box>
            {mutation.error && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {mutation.error instanceof ApiError
                  ? mutation.error.message
                  : 'Attivazione non riuscita'}
              </Alert>
            )}
          </CardContent>
        </Card>
        <Typography color="text.secondary" variant="body2">
          Il link è personale e monouso. Dopo l’attivazione verrai autenticato automaticamente.
        </Typography>
        <SupportFooter />
      </Stack>
    </Container>
  );
}
